import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthProvider.jsx';

// Login + Verify, in the vertical apps' visual language (light, inline styles, no
// Tailwind — these apps deliberately carry no CSS toolchain).
//
// The dashboard keeps its own screens: it is dark, Tailwind, and its login carries
// copy that only makes sense there. Sharing the LOGIC and letting each app render it
// is the split that holds — forcing one look would have been the reason someone
// eventually copied this file instead of importing it.

const sans = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Inter, system-ui, sans-serif';
const T = { ink: '#000', ink2: '#5A5A5A', ink3: '#8C8C8C', hair: '#DCDCDC', wash: '#F5F5F5' };

function Frame({ title, children }) {
  return (
    <div style={{ fontFamily: sans, background: T.wash, color: T.ink, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22 }}>
      <div style={{ width: '100%', maxWidth: 380, background: '#fff', border: `1px solid ${T.hair}`, borderRadius: 12, padding: 26 }}>
        <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.4 }}>Alfred</div>
        <div style={{ fontSize: 21, fontWeight: 600, letterSpacing: -0.6, marginTop: 14 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', font: 'inherit', fontSize: 14, color: T.ink, background: '#fff',
  border: `1px solid ${T.hair}`, borderRadius: 7, padding: '9px 11px', outline: 'none',
  boxSizing: 'border-box', marginTop: 14
};
const buttonStyle = (disabled) => ({
  marginTop: 12, width: '100%', font: 'inherit', fontSize: 13.5, fontWeight: 500,
  background: '#000', color: '#fff', border: '1px solid #000', borderRadius: 7,
  padding: '9px 16px', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1
});

export function Login() {
  const { sendMagicLink, isAuthenticated } = useAuth();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';
  // An invite links here with ?email=, so the person only has to press the button.
  const [email, setEmail] = useState(() => new URLSearchParams(location.search || '').get('email') || '');
  const [sent, setSent] = useState(false);
  // What the SERVER said about whether it can send mail at all. Not the same question
  // as whether this address has access, and it must not be conflated with it.
  const [delivery, setDelivery] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (isAuthenticated) return <Navigate to={from} replace />;

  const submit = async (e) => {
    e.preventDefault();
    const addr = email.trim();
    if (!addr) return;
    setBusy(true); setError('');
    try {
      const out = await sendMagicLink(addr);
      setDelivery((out && (out.delivery || out.data?.delivery)) || null);
      // Say "if that address has access" rather than confirming it exists — an
      // unauthenticated form that distinguishes known from unknown emails is an
      // account-enumeration oracle.
      setSent(true);
    } catch (err) {
      setError(err.message || 'Could not send the link.');
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    // ⛔ TWO DIFFERENT MESSAGES, AND THE DIFFERENCE MATTERS.
    //
    // "If that address has access…" is deliberately vague — saying whether an account
    // exists would make this form an enumeration oracle. But "we cannot send mail at
    // all" is a fact about US, not about the address, and hiding it behind the same
    // reassuring sentence is how a login stays broken for weeks while every screen
    // claims success. That is exactly how the old core-backed sign-in hid itself.
    if (delivery === 'disabled') {
      return (
        <Frame title="No email was sent">
          <p style={{ fontSize: 13.5, color: T.ink2, marginTop: 8, lineHeight: 1.55 }}>
            This server has no mail provider configured, so nothing was sent to anybody.
            The sign-in link was written to the backend logs instead. Set RESEND_API_KEY
            to turn delivery on.
          </p>
          <button style={buttonStyle(false)} onClick={() => { setSent(false); setDelivery(null); }}>
            Back
          </button>
        </Frame>
      );
    }
    return (
      <Frame title="Check your email">
        <p style={{ fontSize: 13.5, color: T.ink2, marginTop: 8, lineHeight: 1.55 }}>
          If {email.trim()} has access, a sign-in link is on its way. It expires shortly —
          open it on this device.
        </p>
        <button style={buttonStyle(false)} onClick={() => { setSent(false); setEmail(''); setDelivery(null); }}>
          Use a different address
        </button>
      </Frame>
    );
  }

  return (
    <Frame title="Sign in">
      <p style={{ fontSize: 13.5, color: T.ink2, marginTop: 8, lineHeight: 1.55 }}>
        We email you a link. No password to remember or lose.
      </p>
      <form onSubmit={submit}>
        <input
          style={inputStyle} type="email" autoComplete="email" placeholder="you@muleworks.co"
          value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email address"
        />
        <button style={buttonStyle(busy || !email.trim())} disabled={busy || !email.trim()} type="submit">
          {busy ? 'Sending…' : 'Email me a link'}
        </button>
      </form>
      {error && <p style={{ fontSize: 12.5, color: '#B00020', marginTop: 10 }}>{error}</p>}
    </Frame>
  );
}

export function Verify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink, isAuthenticated } = useAuth();
  const token = params.get('token');
  const [error, setError] = useState('');
  // A magic-link token is SINGLE USE. React 18 Strict Mode mounts effects twice in
  // development, which would spend the token on the first run and fail on the second —
  // looking exactly like an expired link. The ref makes the POST happen once.
  const tried = useRef(false);

  useEffect(() => {
    if (tried.current) return;
    tried.current = true;
    if (!token) { setError('That link is missing its token.'); return; }
    verifyMagicLink(token)
      .then(() => navigate('/', { replace: true }))
      .catch((err) => setError(err.message || 'That link is no longer valid.'));
  }, [token, verifyMagicLink, navigate]);

  if (isAuthenticated && !error) return <Navigate to="/" replace />;

  return (
    <Frame title={error ? 'That link did not work' : 'Signing you in…'}>
      {error
        ? <>
            <p style={{ fontSize: 13.5, color: T.ink2, marginTop: 8, lineHeight: 1.55 }}>{error}</p>
            <button style={buttonStyle(false)} onClick={() => navigate('/login', { replace: true })}>
              Ask for a new link
            </button>
          </>
        : <p style={{ fontSize: 13.5, color: T.ink3, marginTop: 8 }}>One moment.</p>}
    </Frame>
  );
}
